// features/workstations/property-dossier/dossier-output.js
// ═══════════════════════════════════════════════════════════
// Renders a completed Property Deal Dossier: property snapshot,
// computed deal math, tax strategy, and the Jarvis 5-point
// briefing (when AI synthesis ran). Mirrors the role of
// signal-read/dual-direction-output.js.
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import CompsTable from './comps-table';
import StrategyCompare from './strategy-compare';
import ProjectionTable from './projection-table';
import { CostDonut } from './charts';

const money = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 'n/a';
  return '$' + Math.round(v).toLocaleString();
};
const VERDICT = { buy: ['#7a9e82', 'BUY'], pass: ['#c46050', 'PASS'], conditional: ['#c8a96e', 'CONDITIONAL'] };
const SEV = { high: '#c46050', medium: '#c8a96e', low: '#6b8fa8' };

const cardStyle = { background: 'var(--surface-2)', border: '1px solid var(--border-3)', borderRadius: 12, padding: 20, marginBottom: 16 };
const h = { fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#888', marginBottom: 12 };

function Card({ title, accent, children }) {
  return (
    <section style={cardStyle}>
      <div style={{ ...h, color: accent || '#888' }}>{title}</div>
      {children}
    </section>
  );
}
function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#888' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: color || 'inherit', marginTop: 2 }}>{value}</div>
    </div>
  );
}
const statGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 16 };

function dealMetrics(deal) {
  const a = deal?.analysis || {};
  if (deal?.strategy === 'flip') {
    return [
      { label: 'All-in', value: money(a.allIn) },
      { label: 'Net sale', value: money(a.netSaleProceeds) },
      { label: 'Profit', value: money(a.profit), color: a.profit > 0 ? '#7a9e82' : '#c46050' },
      { label: 'ROI', value: `${a.roiPct}%` },
      { label: 'Margin', value: `${a.marginPct}%` },
    ];
  }
  if (deal?.strategy === 'hold' || deal?.strategy === 'str') {
    return [
      { label: 'Cash invested', value: money(a.cashInvested) },
      { label: 'Cap rate', value: `${a.capRatePct}%` },
      { label: 'Cash-on-cash', value: a.cashOnCashPct == null ? '∞' : `${a.cashOnCashPct}%` },
      { label: 'DSCR', value: a.dscr },
      { label: 'Cash flow', value: `${money(a.monthlyCashFlow)}/mo`, color: a.monthlyCashFlow > 0 ? '#7a9e82' : '#c46050' },
    ];
  }
  // brrrr
  return [
    { label: 'Total project cost', value: money(a.totalProjectCost) },
    { label: 'Refi loan (75% ARV)', value: money(a.refiLoanAmount) },
    { label: 'New equity', value: money(a.newEquity), color: '#7a9e82' },
    { label: 'Cash left in', value: money(a.cashLeftIn), color: a.cashLeftIn <= 5000 ? '#7a9e82' : 'inherit' },
    { label: 'Cash flow', value: `${money(a.monthlyCashFlow)}/mo`, color: a.monthlyCashFlow > 0 ? '#7a9e82' : '#c46050' },
    { label: 'DSCR', value: a.dscr },
  ];
}

export default function DossierOutput({ dossier, onDecision, busy }) {
  const [note, setNote] = useState(dossier?.decision_note || '');
  const [tab, setTab] = useState('overview');
  if (!dossier) return null;

  const synth = dossier.synthesis || null;
  const deal = dossier.deal_analysis || {};
  const tax = dossier.tax_analysis || {};
  const prop = dossier.property_data || {};
  const compare = dossier.compare || null;
  const projection = dossier.projection || null;
  const rule70 = deal.rule70 || {};
  const [vColor, vLabel] = VERDICT[synth?.verdict || dossier.verdict] || VERDICT.conditional;
  // Mock if property data isn't live. Check the explicit flag, then the source
  // string — including the legacy "rentcast:false" format on older rows.
  const src = String(dossier.data_source || prop.source || '');
  const isMock = prop.propertyDataLive === false
    || src.includes('mock')
    || src.includes('rentcast:false');

  return (
    <div style={{ maxWidth: 820 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{dossier.address || dossier.title}</div>
          <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{synth?.summary || dossier.summary || deal.verdict}</div>
        </div>
        <span style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, color: vColor, border: `1px solid ${vColor}` }}>
          {vLabel}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, fontSize: 11, color: '#888' }}>
        <span style={{ textTransform: 'uppercase', color: '#c8a96e' }}>{deal.strategy || dossier.strategy}</span>
        <span>·</span>
        <span style={{ color: isMock ? '#c46050' : '#7a9e82' }}>{isMock ? 'demo data' : 'live data'}</span>
        {(synth?.recommended_strategy || compare?.recommended) && (
          <><span>·</span><span style={{ color: '#9b8fc4' }}>recommended: {String(synth?.recommended_strategy || compare?.recommended).toUpperCase()}</span></>
        )}
        {dossier.llm_skipped && <><span>·</span><span style={{ color: '#c8a96e' }}>computed only (no AI synthesis — add ANTHROPIC_API_KEY)</span></>}
      </div>

      {isMock && (
        <div style={{ background: 'rgba(196,96,80,0.12)', border: '1px solid #c46050', borderRadius: 8, padding: '12px 14px', marginBottom: 18, fontSize: 13, color: '#d98a7e', lineHeight: 1.5 }}>
          ⚠ <b>Demo data — these property figures are NOT real.</b> ARV, comps, rent, beds/baths, and sqft are placeholders scaled to your inputs (RentCast isn’t returning data — the API key isn’t active yet). The deal math + tax numbers are computed correctly <i>off these placeholders</i>, so treat them as illustrative only. <b>What IS live:</b> Census demographics and the Opportunity Zone flag. Add a working <code>RENTCAST_API_KEY</code> to pull real comps/ARV/rent.
        </div>
      )}

      {/* ── tabs: run comps once → click through ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border-3)' }}>
        {[['overview', 'Analysis'], ['fix', 'Fix'], ['mortgage', 'Mortgage'], ['history', 'History']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            style={{ background: 'none', border: 'none', borderBottom: tab === k ? '2px solid #c8a96e' : '2px solid transparent', color: tab === k ? 'inherit' : '#888', padding: '8px 14px', marginBottom: -1, fontSize: 13, fontWeight: tab === k ? 700 : 500, cursor: 'pointer' }}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'fix' && <FixTab prop={prop} />}
      {tab === 'mortgage' && <MortgageTab prop={prop} dealParams={dossier.deal_params} />}
      {tab === 'history' && <HistoryTab prop={prop} />}

      {tab === 'overview' && (<>
      {/* Strategy comparison — the v2 differentiator */}
      {compare && Array.isArray(compare.results) && compare.results.length > 0 && (
        <Card title="Strategy Comparison — same property, every exit" accent="#c8a96e">
          <StrategyCompare compare={compare} />
        </Card>
      )}

      {/* Property snapshot */}
      <Card title="Property">
        <div style={statGrid}>
          <Stat label="Type" value={prop.propertyType || 'n/a'} />
          <Stat label="Beds / Baths" value={`${prop.beds ?? '?'} / ${prop.baths ?? '?'}`} />
          <Stat label="Sqft" value={prop.sqft ? prop.sqft.toLocaleString() : 'n/a'} />
          <Stat label="ARV (est.)" value={money(prop.arv)} />
          <Stat label="Rent (est.)" value={`${money(prop.rentEstimate)}/mo`} />
          {prop.demographics && <Stat label="Median income" value={money(prop.demographics.medianIncome)} />}
          {prop.demographics && <Stat label="Population" value={prop.demographics.populationTrend || 'n/a'} color={prop.demographics.populationTrend === 'decreasing' ? '#c46050' : 'inherit'} />}
        </div>
      </Card>

      {/* Comparable sales */}
      {Array.isArray(prop.comps) && prop.comps.length > 0 && (
        <Card title={`Comparable Sales (${prop.comps.length})`} accent="#6b8fa8">
          <CompsTable comps={prop.comps} subjectSqft={prop.sqft} subjectArv={prop.arv} />
        </Card>
      )}

      {/* Deal math */}
      <Card title="Deal Math" accent="#6b8fa8">
        <div style={{
          display: 'inline-block', padding: '4px 10px', borderRadius: 6, marginBottom: 14, fontSize: 12, fontWeight: 600,
          color: rule70.passes ? '#7a9e82' : '#c46050',
          background: rule70.passes ? 'rgba(122,158,130,0.12)' : 'rgba(196,96,80,0.12)',
        }}>
          70% Rule: {rule70.passes ? 'PASS' : 'FAIL'} — all-in {money(rule70.allIn)} / max {money(rule70.maxAllIn)}
        </div>
        <div style={statGrid}>
          {dealMetrics(deal).map((m) => <Stat key={m.label} {...m} />)}
        </div>
        {deal.analysis?.infiniteReturn && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#7a9e82' }}>♾ Pulls all capital back out — effectively infinite return.</div>
        )}
        {deal.verdict && <div style={{ marginTop: 12, fontSize: 13, color: '#aaa' }}>{deal.verdict}</div>}
      </Card>

      {/* Multi-year projection */}
      {projection && Array.isArray(projection.years) && projection.years.length > 0 && (
        <Card title={`${String(deal.strategy || dossier.strategy || '').toUpperCase()} Projection — ${projection.years.length}-Year`} accent="#6b8fa8">
          <ProjectionTable projection={projection} />
        </Card>
      )}

      {/* Capital sources & uses */}
      {(() => {
        const dp = dossier.deal_params || {};
        const a = deal.analysis || {};
        const slices = [
          { label: 'Purchase', value: Number(dp.purchasePrice) || 0, color: '#4db8b8' },
          { label: 'Rehab', value: Number(dp.rehabCost ?? dp.rehab) || 0, color: '#c8a96e' },
          { label: 'Closing', value: Number(a.closingCosts) || 0, color: '#b8956a' },
          { label: 'Holding', value: (Number(a.holdingInterest) || 0) + (Number(a.holdingCarry) || 0), color: '#6b8fa8' },
        ].filter((s) => s.value > 0);
        return slices.length >= 2 ? (
          <Card title="Capital Sources &amp; Uses" accent="#6b8fa8"><CostDonut slices={slices} /></Card>
        ) : null;
      })()}

      {/* Tax strategy */}
      <Card title="Tax Strategy" accent="#c8a96e">
        <div style={statGrid}>
          <Stat label="First-year benefit (est.)" value={money(tax.firstYearTaxBenefit)} color="#c8a96e" />
          <Stat label="Annual depreciation" value={money(tax.annualDepreciation)} />
          <Stat label="Cost-seg first year" value={money(tax.costSeg?.firstYearTotalDeduction)} />
          {tax.reps?.eligible && <Stat label="REPS active-income savings" value={money(tax.reps.activeIncomeOffsetSavings)} color="#7a9e82" />}
        </div>
        {tax.reps?.note && (
          <div style={{ marginTop: 12, fontSize: 13, color: tax.reps.eligible ? '#7a9e82' : '#c8a96e' }}>
            {tax.reps.note}
          </div>
        )}
        {tax.taxProjection && tax.taxProjection.totalSavings > 0 && (
          <div style={{ marginTop: 12, fontSize: 13 }}>
            <span style={{ color: '#888' }}>Est. tax savings over {tax.taxProjection.holdYears}-yr hold: </span>
            <b style={{ color: '#7a9e82' }}>{money(tax.taxProjection.totalSavings)}</b>
            <span style={{ color: '#666', fontSize: 11 }}> ({tax.taxProjection.years.map((y) => money(y.taxSavings)).join(' · ')})</span>
          </div>
        )}
        {tax.flipWarning && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#c46050' }}>⚠ {tax.flipWarning}</div>
        )}
        {Array.isArray(tax.buyBorrowDieMoves) && tax.buyBorrowDieMoves.length > 0 && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#888' }}>
            Buy-Borrow-Die: {tax.buyBorrowDieMoves.join(' · ')}
          </div>
        )}
        {Array.isArray(tax.strategies) && tax.strategies.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14 }}>
            {tax.strategies.map((s) => (
              <span key={s.id || s.name} title={s.summary}
                style={{ fontSize: 11, padding: '4px 10px', borderRadius: 14, background: 'var(--surface-3)', border: '1px solid var(--border-3)', color: '#bbb' }}>
                {s.name} <span style={{ color: '#666' }}>· {s.primitive}</span>
              </span>
            ))}
          </div>
        )}
      </Card>

      {/* Jarvis 5-point synthesis */}
      {synth && (
        <Card title="Jarvis Briefing">
          {synth.situation && <Para label="Situation" body={synth.situation} />}
          {listBlock('Options', synth.options, (o) => (
            <><b style={{ color: '#c8a96e' }}>{o.action}</b>{o.fit ? ` (${o.fit})` : ''} — {o.rationale}</>
          ))}
          {listBlock('How to structure it', synth.structuring, (o) => (
            <><b>{o.method}</b> — {o.why}</>
          ))}
          {listBlock('Risks & what you’re missing', synth.risks, (o) => (
            <><span style={{ color: SEV[o.severity] || '#888', fontWeight: 600 }}>[{o.severity}]</span> {o.risk}{o.mitigation ? <span style={{ color: '#888' }}> — {o.mitigation}</span> : null}</>
          ))}
          {listBlock('Possibilities', synth.possibilities, (o) => <>{o.insight}</>)}
          {synth.tax_strategy?.headline && (
            <Para label="Tax headline" body={synth.tax_strategy.headline} />
          )}
        </Card>
      )}
      </>)}

      {/* Decision note */}
      <Card title="Your Decision">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Notes on your decision — offer to make, what to verify, pass reason…"
          style={{ width: '100%', minHeight: 70, background: 'var(--surface-3)', border: '1px solid var(--border-2)', borderRadius: 8, padding: 10, color: 'inherit', fontSize: 13, resize: 'vertical' }}
        />
        <button
          onClick={() => onDecision?.({ note })}
          disabled={busy}
          style={{ marginTop: 10, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-2)', background: 'var(--surface-3)', color: 'inherit', cursor: busy ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}
        >
          {busy ? 'Saving…' : 'Save decision'}
        </button>
      </Card>

      {/* Disclaimer */}
      <div style={{ fontSize: 11, color: '#666', marginTop: 6, lineHeight: 1.5 }}>
        {tax.disclaimer || dossier.disclaimer || 'Estimate for education only — not financial or tax advice. Confirm with a CPA / attorney.'}
      </div>
    </div>
  );
}

function Para({ label, body }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: '#888', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}
function listBlock(label, items, render) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: '#888', marginBottom: 6 }}>{label}</div>
      <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((it, i) => <li key={i} style={{ fontSize: 14, lineHeight: 1.5 }}>{render(it)}</li>)}
      </ul>
    </div>
  );
}

// ── Fix + Mortgage tabs — fed by the property data from the comp run ──
const REHAB_PSF = { cosmetic: 25, standard: 45, major: 80, full: 115 };
function amort(P, ratePct, years) {
  const r = (ratePct / 100) / 12, n = years * 12;
  if (!n) return 0;
  if (!r) return P / n;
  return P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}
const inp = { width: '100%', padding: '9px 11px', background: 'var(--surface-3)', border: '1px solid var(--border-2)', borderRadius: 8, color: 'inherit', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
const ilabel = { display: 'block', fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 5 };

function FixTab({ prop }) {
  const [sqft, setSqft] = useState(prop?.sqft || 1500);
  const [scope, setScope] = useState('standard');
  const [contingency, setContingency] = useState(10);
  const base = (parseFloat(sqft) || 0) * REHAB_PSF[scope];
  const total = base * (1 + (parseFloat(contingency) || 0) / 100);
  return (
    <Card title="Fix — Rehab Estimate (Tampa market rates)" accent="#c8a96e">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px,1fr))', gap: 14, marginBottom: 16 }}>
        <label><span style={ilabel}>Square footage</span><input style={inp} value={sqft} onChange={(e) => setSqft(e.target.value)} /></label>
        <label><span style={ilabel}>Scope</span>
          <select style={inp} value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="cosmetic">Cosmetic ~$25/sqft</option>
            <option value="standard">Standard ~$45/sqft</option>
            <option value="major">Major ~$80/sqft</option>
            <option value="full">Full gut ~$115/sqft</option>
          </select>
        </label>
        <label><span style={ilabel}>Contingency %</span><input style={inp} value={contingency} onChange={(e) => setContingency(e.target.value)} /></label>
      </div>
      <div style={statGrid}>
        <Stat label="Base estimate" value={money(base)} />
        <Stat label={`With ${contingency || 0}% contingency`} value={money(total)} color="#c8a96e" />
        <Stat label="Implied $/sqft" value={`$${REHAB_PSF[scope]}`} />
      </div>
      <div style={{ marginTop: 14, fontSize: 12.5, color: '#888', lineHeight: 1.6 }}>
        Market-rate screening estimate — confirm with a contractor bid. <b style={{ color: '#c8a96e' }}>Tampa big-ticket check:</b> roof age · electrical panel · cast-iron/galvanized plumbing · AC age · impact windows · permits — each can add $1,500–5,000 once opened up.
      </div>
    </Card>
  );
}

function MortgageTab({ prop, dealParams }) {
  const [price, setPrice] = useState(Number(dealParams?.purchasePrice) || prop?.arv || 250000);
  const [down, setDown] = useState(20);
  const [rate, setRate] = useState(7);
  const [term, setTerm] = useState(30);
  const [taxIns, setTaxIns] = useState(450);
  const p = parseFloat(price) || 0;
  const loan = p * (1 - (parseFloat(down) || 0) / 100);
  const downAmt = p * ((parseFloat(down) || 0) / 100);
  const pi = amort(loan, parseFloat(rate) || 0, parseFloat(term) || 30);
  const piti = pi + (parseFloat(taxIns) || 0);
  return (
    <Card title="Mortgage — Loan & Payment" accent="#6b8fa8">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px,1fr))', gap: 14, marginBottom: 16 }}>
        <label><span style={ilabel}>Price</span><input style={inp} value={price} onChange={(e) => setPrice(e.target.value)} /></label>
        <label><span style={ilabel}>Down %</span><input style={inp} value={down} onChange={(e) => setDown(e.target.value)} /></label>
        <label><span style={ilabel}>Rate %</span><input style={inp} value={rate} onChange={(e) => setRate(e.target.value)} /></label>
        <label><span style={ilabel}>Term (yrs)</span><input style={inp} value={term} onChange={(e) => setTerm(e.target.value)} /></label>
        <label><span style={ilabel}>Taxes+ins /mo</span><input style={inp} value={taxIns} onChange={(e) => setTaxIns(e.target.value)} /></label>
      </div>
      <div style={statGrid}>
        <Stat label="Loan amount" value={money(loan)} />
        <Stat label="Down payment" value={money(downAmt)} />
        <Stat label="P&I" value={`${money(pi)}/mo`} />
        <Stat label="PITI" value={`${money(piti)}/mo`} color="#6b8fa8" />
      </div>
      <div style={{ marginTop: 14, fontSize: 12.5, color: '#888', lineHeight: 1.6 }}>
        Pre-filled from your deal's purchase price (or ARV). FL note: insurance is a large, rising line — get a real quote before underwriting.
      </div>
    </Card>
  );
}

// ── History tab — ATTOM county records (owner, sales, permits,
//    tax, liens, distress). Live when ATTOM_API_KEY is set; sample
//    data otherwise (flagged). Powers ARV honesty + sourcing. ──
const histRow = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--border-3)', fontSize: 13.5 };
function HistoryTab({ prop }) {
  const o = prop?.owner || {};
  const sales = prop?.salesHistory || [];
  const permits = prop?.permits || [];
  const tax = prop?.taxStatus || {};
  const liens = prop?.liens || [];
  const distress = prop?.distress || [];
  const isMock = !prop?.recordsLive;
  return (
    <>
      {isMock && (
        <div style={{ background: 'rgba(196,96,80,0.10)', border: '1px solid #c46050', borderRadius: 8, padding: '10px 13px', marginBottom: 14, fontSize: 12.5, color: '#d98a7e', lineHeight: 1.5 }}>
          ⚠ Sample county records — set <code>ATTOM_API_KEY</code> for live sales history, permits, liens, tax status &amp; owner data.
        </div>
      )}
      {distress.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {distress.map((d) => (
            <span key={d} style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 14, background: 'rgba(196,96,80,0.12)', border: '1px solid #c46050', color: '#d98a7e' }}>⚑ {d}</span>
          ))}
        </div>
      )}
      <Card title="Owner & Tax" accent="#c8a96e">
        <div style={statGrid}>
          <Stat label="Owner" value={o.name || '—'} />
          <Stat label="Owner-occupied" value={o.occupied ? 'Yes' : 'No — absentee'} color={o.occupied ? undefined : '#c8a96e'} />
          <Stat label="Owned since" value={o.ownedSinceYear || '—'} />
          <Stat label="Annual tax" value={money(tax.annual)} />
          <Stat label="Tax status" value={tax.status || '—'} color={tax.delinquent ? '#c46050' : '#7a9e82'} />
        </div>
      </Card>
      <Card title={`Sale History (${sales.length})`} accent="#6b8fa8">
        {sales.length ? sales.map((s, i) => (
          <div key={i} style={histRow}>
            <span style={{ color: '#888' }}>{s.date}</span>
            <span style={{ fontWeight: 600 }}>{money(s.price)}</span>
            <span style={{ color: '#888', fontSize: 12 }}>{s.deedType}{s.armsLength === false ? ' · non-arm’s-length' : ''}</span>
          </div>
        )) : <div style={{ color: '#888', fontSize: 13 }}>No recorded sales.</div>}
      </Card>
      <Card title={`Permits (${permits.length})`} accent="#c8a96e">
        {permits.length ? permits.map((p, i) => (
          <div key={i} style={histRow}>
            <span style={{ color: '#888' }}>{p.date}</span>
            <span>{p.type}</span>
            <span style={{ color: (p.status === 'Expired' || p.status === 'Open') ? '#c46050' : '#888', fontSize: 12 }}>{p.status}</span>
          </div>
        )) : <div style={{ color: '#888', fontSize: 13 }}>No permits on record.</div>}
        <div style={{ marginTop: 10, fontSize: 12, color: '#888', lineHeight: 1.5 }}>Permit dates hint at systems age (roof/AC) &amp; renovations; expired/open permits = risk + unpermitted-work clues.</div>
      </Card>
      {liens.length > 0 && (
        <Card title={`Liens (${liens.length})`} accent="#c46050">
          {liens.map((l, i) => <div key={i} style={histRow}><span>{l.type}</span><span style={{ fontWeight: 600 }}>{money(l.amount)}</span></div>)}
        </Card>
      )}
    </>
  );
}
